import Ajv2020 from 'ajv/dist/2020';
import flowDefinitionV2Schema from '../../../packages/builder-schema/schemas/flow-definition.v2.schema.json';

let _validate = null;
function getValidateV2() {
  if (!_validate) {
    const ajv = new Ajv2020({ allErrors: true, strict: false });
    _validate = ajv.compile(flowDefinitionV2Schema);
  }
  return _validate;
}

/** JSON Schema validation for flow/v2 (browser-safe; matches Studio JSON tab). */
export function validateStudioV2FlowDefinition(definition) {
  const validate = getValidateV2();
  const ok = validate(definition);
  const errors = (validate.errors || []).map((er) => ({
    instancePath: er.instancePath || '',
    message: er.message || 'invalid',
  }));
  return { valid: !!ok, errors };
}

/**
 * Kahn topological sort — if not all nodes processed, a directed cycle exists.
 */
export function detectDirectedCycles(nodeIds, edges) {
  const ids = new Set(nodeIds);
  if (ids.size === 0) return { hasCycle: false };
  const adj = new Map();
  const indeg = new Map();
  for (const id of ids) {
    adj.set(id, []);
    indeg.set(id, 0);
  }
  for (const e of edges) {
    if (!e || !ids.has(e.source) || !ids.has(e.target)) continue;
    adj.get(e.source).push(e.target);
    indeg.set(e.target, indeg.get(e.target) + 1);
  }
  const q = [...ids].filter((id) => indeg.get(id) === 0);
  let cnt = 0;
  while (q.length) {
    const v = q.shift();
    cnt += 1;
    for (const w of adj.get(v) || []) {
      indeg.set(w, indeg.get(w) - 1);
      if (indeg.get(w) === 0) q.push(w);
    }
  }
  return { hasCycle: cnt < ids.size };
}

/** Static analysis for editor JSON (schema + graph checks); mirrors @chatbot/builder-engine analyzeFlowGraph. */
export function analyzeStudioV2FlowGraph(definition) {
  const errors = [];
  const warnings = [];
  const schema = validateStudioV2FlowDefinition(definition || {});
  if (!schema.valid) {
    for (const er of schema.errors || []) {
      const p = er.instancePath || '/';
      errors.push(`${p}: ${er.message || 'invalid'}`);
    }
  }

  const nodes = Array.isArray(definition?.nodes) ? definition.nodes : [];
  const edges = Array.isArray(definition?.edges) ? definition.edges : [];
  const ids = new Set();

  for (const n of nodes) {
    if (!n || typeof n !== 'object') {
      errors.push('Invalid node entry');
      continue;
    }
    if (!n.id) {
      errors.push('Node missing id');
      continue;
    }
    if (ids.has(n.id)) errors.push(`Duplicate node id: ${n.id}`);
    ids.add(n.id);
  }

  for (const e of edges) {
    if (!e || typeof e !== 'object') {
      errors.push('Invalid edge entry');
      continue;
    }
    if (!e.source || !e.target) {
      errors.push('Edge missing source or target');
      continue;
    }
    if (!ids.has(e.source)) errors.push(`Edge unknown source: ${e.source}`);
    if (!ids.has(e.target)) errors.push(`Edge unknown target: ${e.target}`);
  }

  const { hasCycle } = detectDirectedCycles(ids, edges);
  if (hasCycle) warnings.push('Directed cycle detected (control-flow may be invalid for Studio).');

  const startIds = nodes.filter((n) => n.type === 'start').map((n) => n.id);
  if (startIds.length === 0) warnings.push('No start node');
  if (startIds.length > 1) warnings.push('Multiple start nodes');

  const reachable = new Set();
  const stack = [...startIds];
  while (stack.length) {
    const v = stack.pop();
    if (reachable.has(v)) continue;
    reachable.add(v);
    for (const e of edges) {
      if (e.source === v && ids.has(e.target)) stack.push(e.target);
    }
  }
  for (const id of ids) {
    const node = nodes.find((n) => n.id === id);
    if (node?.type === 'start') continue;
    if (!reachable.has(id)) warnings.push(`Node not reachable from start: ${id}`);
  }

  return {
    schemaOk: schema.valid,
    hasCycle,
    errors,
    warnings,
    nodeCount: nodes.length,
    edgeCount: edges.length,
  };
}
